import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles } from 'lucide-react';
import { ChatMessage } from '../types';
import { API_BASE_URL } from '../services/api';

interface ChatPanelProps {
  context?: string;
  onClose?: () => void;
  externalMessages?: ChatMessage[];
  onMessagesChange?: (messages: ChatMessage[]) => void;
  onClearHistory?: () => void;
}

const defaultWelcomeMessage: ChatMessage = { 
  role: 'assistant', 
  content: 'Hi! I\'m your Data Science tutor. Feel free to ask me anything about the course material!' 
};

export default function ChatPanel({ context, onClose, externalMessages, onMessagesChange, onClearHistory }: ChatPanelProps) {
  const isControlled = externalMessages !== undefined;
  
  // Internal state for uncontrolled mode
  const [internalMessages, setInternalMessages] = useState<ChatMessage[]>([defaultWelcomeMessage]);
  const messages = isControlled ? externalMessages! : internalMessages;
  
  const setMessages = (newMessages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    if (isControlled) {
      const resolvedMessages = typeof newMessages === 'function' 
        ? newMessages(externalMessages!)
        : newMessages;
      onMessagesChange?.(resolvedMessages);
    } else {
      setInternalMessages(newMessages);
    }
  };

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Use streaming response
      const response = await fetch(`${API_BASE_URL}/student/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          messages: newMessages,
          context,
          stream: true,
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      let assistantContent = '';
      setMessages([...newMessages, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        assistantContent += chunk;
        
        setMessages([
          ...newMessages,
          { role: 'assistant', content: assistantContent },
        ]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages([
        ...newMessages,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-apple-card border-l border-apple-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-apple-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-apple-purple to-apple-blue rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-apple-text">AI Tutor</h3>
            <p className="text-xs text-apple-text-secondary">Ask anything about the material</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Lambda Link */}
          <a
            href="https://lambda.com.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-apple-gray transition-colors group"
            title="Visit Lambda AI"
          >
            <img
              src="https://lambda.com.ai/LAMBDA_logo.gif"
              alt="Lambda AI"
              className="w-8 h-8 object-contain"
            />
            <span className="text-xs font-medium text-apple-text-secondary group-hover:text-apple-text hidden sm:inline">LAMBDA</span>
          </a>
          
          {onClearHistory && messages.length > 1 && (
            <button
              onClick={onClearHistory}
              className="p-2 text-apple-text-secondary hover:text-apple-red hover:bg-red-50 rounded-full transition-colors text-xs"
              title="Clear history"
            >
              Clear
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-apple-gray rounded-full transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              message.role === 'user' ? 'bg-apple-blue' : 'bg-gradient-to-br from-apple-purple to-apple-blue'
            }`}>
              {message.role === 'user' ? (
                <User className="w-4 h-4 text-white" />
              ) : (
                <Bot className="w-4 h-4 text-white" />
              )}
            </div>
            <div className={`chat-message ${message.role}`}>
              <div className="whitespace-pre-wrap">{message.content}</div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-apple-purple to-apple-blue flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="chat-message assistant">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-apple-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-apple-text-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-apple-text-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-apple-border">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            className="w-full pr-12 pl-4 py-3 rounded-apple-sm bg-apple-gray border-0 resize-none focus:ring-2 focus:ring-apple-blue/30 focus:outline-none"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-apple-blue text-white rounded-lg hover:bg-apple-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-2 text-xs text-apple-text-secondary text-center">
          AI can make mistakes. Please verify important information.
        </p>
      </div>
    </div>
  );
}
